/**
 * RecordConflictModal — konflikt wersji rekordu lokalu w formularzu panelu (409 RECORD_CHANGED).
 * Lustro decyzji z tabletu (mobile/src/components/RecordConflictModal.tsx, Bronek 2026-10-09).
 *
 * Nic nie dzieje się samo: zamknięcie okna (×, Esc, „Anuluj") wraca do formularza bez zapisu.
 * „Wczytaj aktualną wersję" odrzuca zmiany z formularza dopiero po drugim potwierdzeniu;
 * „Zapisz moją wersję" nadpisuje tylko po jawnym kliknięciu (fokus startuje na „Anuluj", żeby Enter
 * niczego nie nadpisał). Komponent tylko pokazuje i zbiera decyzję — zapis robi formularz.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ModalShell } from '../ds/ModalShell';
import { ConfirmModal } from '../ds/ConfirmModal';
import { Button } from '../ds/components/Button';
import type { RecordDiffRow, ServerApartmentRecord } from '../utils/recordConflict';

interface Props {
  open: boolean;
  apartmentNumber: string;
  /** Wersja z serwera z odpowiedzi 409 (brak = serwer jej nie podał). */
  server: ServerApartmentRecord | null;
  diff: RecordDiffRow[];
  /** Trwa zapis — przyciski działań zablokowane. */
  busy?: boolean;
  onLoadServer: () => void;
  onSaveMine: () => void;
  onClose: () => void;
}

function formatVersion(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const cell: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: 'var(--border-width) solid var(--border)',
  textAlign: 'left',
  verticalAlign: 'top',
  overflowWrap: 'anywhere',
  whiteSpace: 'pre-wrap',
};
const headCell: React.CSSProperties = {
  ...cell,
  color: 'var(--text-secondary)',
  fontWeight: 'var(--weight-semibold)',
  whiteSpace: 'nowrap',
};

export function RecordConflictModal({ open, apartmentNumber, server, diff, busy = false, onLoadServer, onSaveMine, onClose }: Props) {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Esc zamyka TYLKO to okno. Każdy ModalShell słucha Esc na `document` (capture), więc bez tego Esc
  // zamykał też formularz lokalu i widok budynku pod spodem — z niezapisanymi zmianami, które to okno
  // chroni. Przechwytujemy o poziom wyżej (`window`, capture), jak ds/ConfirmModal. Gdy otwarte jest
  // pytanie „Odrzucić zmiany?", Esc należy do niego (ConfirmModal ma własny handler na `window`).
  useEffect(() => {
    if (!open || confirmDiscard) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open, confirmDiscard]);

  const savedAt = formatVersion(server?.updatedAt);
  const canSaveMine = !!server?.updatedAt;

  return (
    <>
      <ModalShell
        open={open}
        onClose={onClose}
        title="Lokal zmieniony na innym urządzeniu"
        width={760}
        footer={
          // Trzy przyciski nie mieszczą się w jednym rzędzie na wąskim ekranie — zawijamy.
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 'var(--space-3)', width: '100%' }}>
            <Button variant="secondary" onClick={onClose} disabled={busy} data-autofocus>
              Anuluj
            </Button>
            <Button variant="secondary" icon="refresh" onClick={() => setConfirmDiscard(true)} disabled={busy}>
              Wczytaj aktualną wersję
            </Button>
            <Button variant="primary" icon="check" onClick={onSaveMine} disabled={busy || !canSaveMine}>
              {busy ? 'Zapisywanie...' : 'Zapisz moją wersję'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <p style={{ margin: 0 }}>
            Rekord lokalu {apartmentNumber} został zmieniony (np. na tablecie) po otwarciu tego formularza.
            Twoje zmiany <strong>nie zostały zapisane</strong> — wybierz, która wersja ma zostać.
          </p>
          {savedAt && (
            <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
              Wersja na serwerze: {savedAt}
              {server?.savedBy ? ` (zapisał: ${server.savedBy})` : ''}
            </div>
          )}
          {diff.length > 0 ? (
            <div style={{ overflow: 'auto', maxHeight: '45vh' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-label)' }}>
                <thead>
                  <tr>
                    <th style={headCell}>Pole</th>
                    <th style={headCell}>Na serwerze</th>
                    <th style={headCell}>W tym formularzu</th>
                  </tr>
                </thead>
                <tbody>
                  {diff.map((r) => (
                    <tr key={r.label}>
                      <td style={{ ...cell, color: 'var(--text-secondary)' }}>{r.label}</td>
                      <td style={cell}>{r.server}</td>
                      <td style={cell}>{r.mine}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
              Pola widoczne w tym formularzu się nie różnią — zmiana dotyczy danych, których panel tu nie pokazuje
              (np. daty wizyty).
            </div>
          )}
          <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
            „Zapisz moją wersję” zapisze pola z tego formularza na wersji z serwera; źródła ciepła i podpis
            lokatora zapisane na tablecie zostają. „Wczytaj aktualną wersję” pokaże w formularzu wersję z serwera.
          </div>
        </div>
      </ModalShell>

      <ConfirmModal
        open={open && confirmDiscard}
        title="Odrzucić zmiany z formularza?"
        message="Zmiany wprowadzone w tym formularzu zostaną utracone, a formularz pokaże aktualną wersję zapisaną na serwerze."
        confirmLabel="Odrzuć i wczytaj"
        cancelLabel="Wróć"
        destructive
        onConfirm={() => {
          setConfirmDiscard(false);
          onLoadServer();
        }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </>
  );
}
